import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Grid, Typography, Link, Tooltip, IconButton,
} from "@mui/material";
import { FileUploadOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import templateFile from "assets/SAMPLE_VENDOR_LEAD_IMPORT.xlsx";

const COLUMN_MAP = {
    "Name": "name",
    "Company Name": "companyName",
    "Contact Person Name": "contactPersonName",
    "Phone": "phone",
    "Alternate Phone": "alternatePhone",
    "Email": "email",
    "Address": "address",
    "Industry Type": "industryType",
    "Current Provider": "currentProvider",
    "Estimated Monthly Volume": "estimatedMonthlyVolume",
    "Preferred Service Type": "preferredServiceType",
    "Pain Points": "painPoints",
    "Preferred Routes": "preferredRoutes",
};

const UploadVendorLeadDialog = () => {
    const [showDialog, setShowDialog] = useState(false);
    const [fileData, setFileData] = useState(null);
    const navigate = useNavigate();

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const ext = file.name.split(".").pop().toLowerCase();
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const workbook = ext === "csv"
                    ? XLSX.read(e.target.result, { type: "string" })
                    : XLSX.read(new Uint8Array(e.target.result), { type: "array" });

                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

                // Map Excel columns to model field names
                const mapped = json.map((row) => {
                    const lead = {};
                    Object.entries(COLUMN_MAP).forEach(([excelCol, field]) => {
                        lead[field] = row[excelCol] ?? "";
                    });
                    // Split comma-separated array fields
                    lead.painPoints = lead.painPoints
                        ? String(lead.painPoints).split(",").map((s) => s.trim()).filter(Boolean)
                        : [];
                    lead.preferredRoutes = lead.preferredRoutes
                        ? String(lead.preferredRoutes).split(",").map((s) => s.trim()).filter(Boolean)
                        : [];
                    lead.estimatedMonthlyVolume = lead.estimatedMonthlyVolume
                        ? Number(lead.estimatedMonthlyVolume) : undefined;
                    return lead;
                });

                setFileData(mapped);
            } catch (err) {
                console.error(err);
                alert("Invalid file. Please upload a valid Excel or CSV file.");
            }
        };

        ext === "csv" ? reader.readAsText(file) : reader.readAsArrayBuffer(file);
    };

    const handleReview = () => {
        if (!fileData || fileData.length === 0) {
            alert("Please upload a valid file first.");
            return;
        }
        navigate("/app/crm/vendor-lead/bulk-review", { state: { leads: fileData } });
        setShowDialog(false);
    };

    return (
        <>
            <Tooltip title="Bulk Import Leads" arrow>
                <IconButton color="primary" onClick={() => setShowDialog(true)}>
                    <FileUploadOutlined />
                </IconButton>
            </Tooltip>

            <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Bulk Import Leads</DialogTitle>
                <DialogContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                Upload an Excel or CSV file with lead data.
                            </Typography>
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={handleFileUpload}
                            />
                            {fileData && (
                                <Typography variant="caption" color="success.main" mt={1} display="block">
                                    {fileData.length} row(s) loaded
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">
                                Download the template:{" "}
                                <Link href={templateFile} download="SAMPLE_VENDOR_LEAD_IMPORT.xlsx">
                                    SAMPLE_VENDOR_LEAD_IMPORT.xlsx
                                </Link>
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDialog(false)} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleReview} variant="contained" disabled={!fileData}>
                        Review
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UploadVendorLeadDialog;